import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { es } from "@/i18n/es";
import { NewsTicker } from "@/knowmeno/news/NewsTicker";
import { LocaleSwitcher } from "@/knowmeno/marketing/LocaleSwitcher";
import { ArrowRight, Activity, LineChart, Sparkles, ShieldCheck, Heart } from "lucide-react";

export const Route = createFileRoute("/es/")({
  head: () => ({
    meta: [
      { title: "SepaMeno — Tu compañera para la menopausia" },
      {
        name: "description",
        content:
          "Seguimiento diario, ideas con IA y un Reporte Inteligente de Síntomas para llevar a tu doctora.",
      },
      { property: "og:title", content: "SepaMeno — Tu compañera para la menopausia" },
      {
        property: "og:description",
        content:
          "Seguimiento diario, ideas con IA y un Reporte Inteligente de Síntomas que crece contigo.",
      },
      { property: "og:url", content: "https://sepameno.com/" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "es" },
      { property: "og:locale:alternate", content: "en" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { httpEquiv: "content-language", content: "es" },
    ],
    links: [
      { rel: "canonical", href: "https://sepameno.com/" },
      { rel: "alternate", hrefLang: "es", href: "https://sepameno.com/" },
      { rel: "alternate", hrefLang: "en", href: "https://knowmeno.com/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://knowmeno.com/" },
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "¿Tengo que tomar terapia hormonal?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "No. SepaMeno no promueve ningún tratamiento en particular. El Reporte Inteligente apoya cualquier conversación — terapia hormonal, opciones no hormonales, cambios de estilo de vida o pruebas adicionales. Te da a ti y a tu doctora la información para encontrar lo que funciona para TI.",
            },
          },
          {
            "@type": "Question",
            name: "¿Cuándo recibo mi Reporte Inteligente?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Desde el Día 1 ya tienes tu Puntaje de Menopausia y una idea diaria. Al Día 7 el mapa de síntomas empieza a llenarse. El reporte completo en PDF se desbloquea al Día 14 — es el mínimo confiable para que una doctora pueda actuar. Al Día 90 tienes la versión más completa: tendencias, respuesta a terapia y tu ciclo completo.",
            },
          },
          {
            "@type": "Question",
            name: "¿SepaMeno reemplaza a mi doctora?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "No. SepaMeno es una herramienta personal de seguimiento — no un dispositivo médico. Te ayuda a organizar los síntomas, patrones y preguntas que llevas a tu consulta. Siempre trabaja con un profesional de salud calificado.",
            },
          },
          {
            "@type": "Question",
            name: "¿Puedo cancelar cuando quiera?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Sí — en cualquier momento, con un toque desde la configuración de tu cuenta. Sin llamadas, sin sentimientos de culpa.",
            },
          },
          {
            "@type": "Question",
            name: "¿Quién está detrás de SepaMeno?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Somos un equipo pequeño de mujeres que hemos vivido esto — y que nos cansamos de que nos dijeran que era 'solo estrés'.",
            },
          },
        ],
      }),
    }],
  }),
  component: SepaMenoHome,
});

const PLUM = "#4B1F4F";
const ROSE = "#FF5C7A";
const CREAM = "#FAF7F5";
const INK = "#4B1F4F";
const MUTED = "#6E4B71";
const SANS = '"Inter", system-ui, -apple-system, sans-serif';
const SERIF = '"Cormorant Garamond", "Playfair Display", Georgia, serif';

function SepaMenoHome() {
  const t = es.home;
  return (
    <div style={{ background: CREAM, color: INK, fontFamily: SANS, minHeight: "100vh" }} lang="es">
      <Nav />
      <NewsTicker />
      <Hero t={t} />
      <Pillars t={t} />
      <MedicalResetTeaser t={t} />
      <FoundersNote t={t} />
      <FAQ t={t} />
      <Footer t={t} />
    </div>
  );
}

function Nav() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const linkStyle: React.CSSProperties = {
    color: PLUM, fontSize: 14, fontWeight: 400, textDecoration: "none",
    cursor: "pointer", fontFamily: SANS,
  };
  const ctaStyle: React.CSSProperties = {
    background: PLUM, color: "#fff", fontSize: 13, fontWeight: 500,
    padding: "9px 18px", borderRadius: 999, textDecoration: "none",
    display: "inline-block", fontFamily: SANS,
  };

  return (
    <header style={{
      position: "sticky", top: 0, zIndex: 100, background: "#FFFFFF",
      borderBottom: "0.5px solid rgba(0,0,0,0.06)",
    }}>
      <nav style={{
        maxWidth: 1200, margin: "0 auto", padding: "16px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Link to="/es" style={{ textDecoration: "none", display: "inline-flex", alignItems: "baseline" }}>
          <span style={{ fontFamily: SANS, fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em" }}>
            <span style={{ color: PLUM }}>Sepa</span><span style={{ color: ROSE }}>Meno</span>
            <sup style={{ color: ROSE, fontSize: "0.38em", marginLeft: "0.1em" }}>™</sup>
          </span>
        </Link>

        <div className="es-nav-desktop" style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <Link to="/es/guia-menopausia" style={linkStyle}>{es.nav.guide}</Link>
          <Link to="/es/medical-reset" style={linkStyle}>{es.nav.medicalReset}</Link>
          <Link to="/es/noticias" style={linkStyle}>Noticias</Link>
          <LocaleSwitcher />
          <Link to="/sign-in" style={{ ...linkStyle, color: MUTED }}>{es.nav.signIn}</Link>
          <Link to="/sign-up" style={ctaStyle}>{es.nav.getStarted}</Link>
        </div>

        <button
          className="es-nav-burger"
          aria-label="Abrir menú"
          onClick={() => setOpen(true)}
          style={{ display: "none", background: "none", border: "none", padding: 8, cursor: "pointer", color: PLUM, fontSize: 22 }}
        >☰</button>
      </nav>

      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200 }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            position: "absolute", top: 0, right: 0, width: "min(320px, 85vw)", height: "100%",
            background: "#FFFFFF", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 8,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <LocaleSwitcher />
              <button aria-label="Cerrar" onClick={() => setOpen(false)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: PLUM }}>×</button>
            </div>
            <Link to="/es/guia-menopausia" onClick={() => setOpen(false)} style={{ ...linkStyle, padding: "14px 4px", fontSize: 16 }}>{es.nav.guide}</Link>
            <Link to="/es/medical-reset" onClick={() => setOpen(false)} style={{ ...linkStyle, padding: "14px 4px", fontSize: 16 }}>{es.nav.medicalReset}</Link>
            <Link to="/es/noticias" onClick={() => setOpen(false)} style={{ ...linkStyle, padding: "14px 4px", fontSize: 16 }}>Noticias</Link>
            <Link to="/sign-in" onClick={() => setOpen(false)} style={{ ...linkStyle, color: MUTED, padding: "14px 4px", fontSize: 16 }}>{es.nav.signIn}</Link>
            <Link to="/sign-up" onClick={() => setOpen(false)} style={{ ...ctaStyle, marginTop: 16, textAlign: "center", padding: "12px 18px", fontSize: 14 }}>{es.nav.getStarted}</Link>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .es-nav-desktop { display: none !important; }
          .es-nav-burger { display: inline-flex !important; }
        }
      `}</style>
    </header>
  );
}

function Hero({ t }: { t: typeof es.home }) {
  return (
    <section style={{ maxWidth: 1100, margin: "0 auto", padding: "64px 24px 48px" }}>
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,92,122,0.12)",
        color: ROSE, padding: "6px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600,
        letterSpacing: "0.06em", textTransform: "uppercase",
      }}>
        <Sparkles size={14} /> {t.heroEyebrow}
      </span>
      <h1 style={{
        fontFamily: SERIF, fontWeight: 500, fontSize: "clamp(40px, 7vw, 72px)",
        lineHeight: 1.05, margin: "20px 0 16px", color: INK, letterSpacing: "-0.02em",
      }}>
        {t.heroTitle}
      </h1>
      <p style={{ fontSize: 18, lineHeight: 1.6, color: MUTED, maxWidth: 640, margin: 0 }}>
        {t.heroSub}
      </p>
      <div style={{ marginTop: 32, display: "flex", flexWrap: "wrap", gap: 12 }}>
        <Link to="/sign-up" style={{
          background: PLUM, color: "#fff", padding: "14px 28px", borderRadius: 999,
          fontSize: 15, fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8,
        }}>{t.heroCtaPrimary} <ArrowRight size={16} /></Link>
        <Link to="/es/medical-reset" style={{
          background: "transparent", color: PLUM, padding: "14px 28px", borderRadius: 999,
          fontSize: 15, fontWeight: 500, textDecoration: "none", border: `1px solid ${PLUM}`,
        }}>{t.heroCtaSecondary}</Link>
      </div>
    </section>
  );
}

function Pillars({ t }: { t: typeof es.home }) {
  const icons = [<Heart size={24} />, <Activity size={24} />, <LineChart size={24} />];
  return (
    <section style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 24px" }}>
      <h2 style={{ fontFamily: SERIF, fontSize: "clamp(28px, 4vw, 40px)", color: INK, margin: "0 0 32px" }}>
        {t.pillarsTitle}
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
        {t.pillars.map((p, i) => (
          <div key={i} style={{
            background: "#fff", border: "1px solid #E8D5DA", borderRadius: 16, padding: 28,
          }}>
            <div style={{ color: ROSE, marginBottom: 12 }}>{icons[i]}</div>
            <h3 style={{ fontFamily: SERIF, fontSize: 22, margin: "0 0 8px", color: INK }}>{p.title}</h3>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: MUTED, margin: 0 }}>{p.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function MedicalResetTeaser({ t }: { t: typeof es.home }) {
  return (
    <section style={{ background: "#fff", borderTop: "1px solid #E8D5DA", borderBottom: "1px solid #E8D5DA", marginTop: 40 }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "64px 24px" }}>
        <span style={{ color: ROSE, fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Reset Médico
        </span>
        <h2 style={{ fontFamily: SERIF, fontSize: "clamp(32px, 5vw, 48px)", margin: "12px 0 16px", color: INK, lineHeight: 1.1 }}>
          {t.medicalResetTitle}
        </h2>
        <p style={{ fontSize: 17, lineHeight: 1.6, color: MUTED, maxWidth: 720, margin: 0 }}>
          {t.medicalResetBody}
        </p>
        <Link to="/es/medical-reset" style={{
          marginTop: 24, display: "inline-flex", alignItems: "center", gap: 8,
          color: PLUM, fontSize: 15, fontWeight: 600, textDecoration: "none",
        }}>{t.medicalResetCta} <ArrowRight size={16} /></Link>
      </div>
    </section>
  );
}

function FoundersNote({ t }: { t: typeof es.home }) {
  return (
    <section style={{ maxWidth: 800, margin: "0 auto", padding: "64px 24px", textAlign: "center" }}>
      <span style={{ color: ROSE, fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {t.foundersEyebrow}
      </span>
      <blockquote style={{
        fontFamily: SERIF, fontSize: "clamp(24px, 3.5vw, 32px)", lineHeight: 1.3,
        color: INK, margin: "20px 0 0", fontStyle: "italic",
      }}>
        “{t.foundersQuote}”
      </blockquote>
    </section>
  );
}

function FAQ({ t }: { t: typeof es.home }) {
  return (
    <section style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px 64px" }}>
      <h2 style={{ fontFamily: SERIF, fontSize: "clamp(28px, 4vw, 40px)", color: INK, margin: "0 0 32px" }}>
        {t.faqTitle}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {t.faq.map((item, i) => (
          <details key={i} style={{
            background: "#fff", border: "1px solid #E8D5DA", borderRadius: 12, padding: "18px 22px",
          }}>
            <summary style={{ cursor: "pointer", fontSize: 16, fontWeight: 500, color: INK, listStyle: "none" }}>
              {item.q}
            </summary>
            <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.6, color: MUTED }}>{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function Footer({ t }: { t: typeof es.home }) {
  return (
    <footer style={{ background: "#fff", borderTop: "1px solid #E8D5DA", padding: "40px 24px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", flexWrap: "wrap", gap: 24, justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: SANS, fontSize: 18, fontWeight: 600 }}>
            <span style={{ color: PLUM }}>Sepa</span><span style={{ color: ROSE }}>Meno</span>
          </div>
          <p style={{ fontSize: 13, color: MUTED, margin: "8px 0 0", fontStyle: "italic" }}>{t.footerTagline}</p>
        </div>
        <div style={{ display: "flex", gap: 20, fontSize: 13 }}>
          <Link to="/es/privacidad" style={{ color: MUTED, textDecoration: "none" }}>Privacidad</Link>
          <Link to="/es/terminos" style={{ color: MUTED, textDecoration: "none" }}>Términos</Link>
          <Link to="/es/aviso-legal" style={{ color: MUTED, textDecoration: "none" }}>Aviso legal</Link>
        </div>
      </div>
      <p style={{ maxWidth: 1100, margin: "24px auto 0", fontSize: 12, color: MUTED, display: "flex", alignItems: "center", gap: 6 }}>
        <ShieldCheck size={14} /> {t.trustLine}
      </p>
    </footer>
  );
}
